import { Adresse } from "codegen";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import TextField, { TextFieldProps } from "components/organisms/form/TextField";
import { MaalerAdresse } from "types/strombestilling-api";
import searchAddress, { GeoNorgeAddress } from "utils/GeoNorge";

export function geoNorgeAddressToMaalerAdresse(address: GeoNorgeAddress): MaalerAdresse {
	return {
		gate: address.gate || "",
		husnummer: (address.husnummer || "") + (address.bokstav || ""),
		postnummer: address.postnummer || "",
		poststed: address.poststed || "",
		asString: address.resultat || "",
	};
}

/** Feilmelding hvis fakturaadressen mangler felt, ellers null. Ingen adresse gir også null. */
export function validateFakturaAdresse(fakturaAdresse: Adresse | null): string | null {
	if (fakturaAdresse === null) {
		return null;
	}
	const missing: string[] = [];
	if (fakturaAdresse.postnummer?.length !== 4) {
		if (fakturaAdresse.postnummer) missing.push(`postnummer (lengden må være 4 karakterer, var ${fakturaAdresse.postnummer.length})`);
		else missing.push("postnummer");
	}

	if (!fakturaAdresse.gate || fakturaAdresse.gate.length === 0) {
		missing.push("gate");
	}
	if (!fakturaAdresse.poststed || fakturaAdresse.poststed.length === 0) {
		missing.push("poststed");
	}
	if (missing.length === 0) {
		return null;
	}
	return `Fakturaadressen er ufullstendig og mangler [${missing.join(", ")}]. Vennligst velg en fakturaadresse fra nedtrekksmenyen`;
}

interface AddressSearchTextFieldProps extends TextFieldProps {
	setAddress?: (address: GeoNorgeAddress | null) => void;
}

export default function AddressSearchTextField({ setAddress, ...props }: Readonly<AddressSearchTextFieldProps>) {
	const [searchQuery, _setSearchQuery] = useState<string>((props.defaultValue as string) || "");
	// Opprett debounce funksjon én gang
	const setSearchQuery = useRef(
		debounce((updatedQuery: string) => {
			_setSearchQuery(updatedQuery);
		}, 300)
	).current;

	// Cancel debounce funksjon ved unmount
	useEffect(() => {
		return () => {
			setSearchQuery.cancel();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const inputRef = useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>;
	const [activeIndex, setActiveIndex] = useState(-1); // -1 betyr ingen er valgt

	const [searchResults, setSearchResults] = useState<GeoNorgeAddress[]>([]);
	const [showResults, setShowResults] = useState(false);

	useEffect(() => {
		async function fetchAndSetSearchResults() {
			await searchAddress(searchQuery, (addresses) => {
				setSearchResults(addresses);
			});
		}
		if (searchQuery.trim() !== "") {
			fetchAndSetSearchResults();
		}
	}, [searchQuery]);

	useEffect(() => {
		if (!showResults || searchResults.length === 0) {
			setActiveIndex(-1);
		}
	}, [showResults, searchResults]);

	const selectAddress = useCallback(
		(address: GeoNorgeAddress) => {
			setAddress?.(address);
			setShowResults(false);
			if (inputRef.current && address.resultat) {
				inputRef.current.value = address.resultat;
			}
			setActiveIndex(-1);
		},
		[setAddress, inputRef]
	);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				if (searchResults.length > 0) {
					const nextIndex = (activeIndex + 1) % searchResults.length;
					setActiveIndex(nextIndex);
					setShowResults(true); // Ensure results are visible when navigating
				}
				break;
			case "ArrowUp":
				event.preventDefault();
				if (searchResults.length > 0) {
					const prevIndex = (activeIndex - 1 + searchResults.length) % searchResults.length;
					setActiveIndex(prevIndex);
					setShowResults(true); // Ensure results are visible when navigating
				}
				break;
			case "Enter":
				if (activeIndex !== -1 && searchResults[activeIndex]) {
					event.preventDefault();
					selectAddress(searchResults[activeIndex]);
				} else if (inputRef.current?.value) {
					// If no item is selected but there's text, perform a search or do nothing
					// For now, we'll just prevent default to avoid form submission if it's part of a form
					event.preventDefault();
				}
				break;
			case "Escape":
				event.preventDefault();
				setShowResults(false);
				setActiveIndex(-1);
				break;
			default:
				break;
		}
	};

	return (
		<Wrapper>
			<TextField
				type="text"
				autoComplete="off"
				role="combobox"
				aria-autocomplete="list"
				aria-controls="address-search-results"
				aria-expanded={showResults && searchResults.length > 0}
				aria-activedescendant={activeIndex !== -1 ? `address-option-${activeIndex}` : undefined}
				onChange={(e) => {
					props.onChange?.(e);
					setAddress?.(null);
					const value = inputRef.current?.value || "";
					if (value === "") {
						setSearchResults([]);
						setShowResults(false);
					} else {
						setSearchQuery(value);
						setShowResults(true);
					}
					setActiveIndex(-1); // Reset active index on text change
				}}
				onFocus={(e) => {
					props.onFocus?.(e);
					const value = inputRef.current?.value || "";
					if (value === "") {
						setSearchResults([]);
						setShowResults(false);
					} else {
						setSearchQuery(value);
						setShowResults(true);
					}
				}}
				onBlur={(e) => {
					props.onBlur?.(e);
					setTimeout(() => {
						setShowResults(false);
						setActiveIndex(-1);
					}, 400);
				}}
				onKeyDown={handleKeyDown}
				inputRef={inputRef}
				{...props}
			/>
			{showResults && (
				<Addresses role="listbox" id="address-search-results">
					{searchResults.length > 0 ? (
						searchResults.map((result, index) => (
							<Address
								key={result.resultat}
								id={`address-option-${index}`}
								role="option"
								aria-selected={index === activeIndex}
								$isActive={index === activeIndex}
								onClick={() => selectAddress(result)}
							>
								{result.resultat}
							</Address>
						))
					) : (
						<NoAddress role="alert" aria-live="polite">
							Ingen adresser funnet
						</NoAddress>
					)}
				</Addresses>
			)}
		</Wrapper>
	);
}

const Wrapper = styled.div`
	position: relative;
`;

const Addresses = styled.div`
	position: absolute;
	top: 70px;
	display: flex;
	flex-direction: column;
	background-color: var(--white);
	border: 1px solid var(--nte-graa-400);
	border-radius: 4px;
`;

const Address = styled.button<{ $isActive: boolean }>`
	padding: 8px;
	text-align: left;
	background-color: var(--white);
	color: var(--nte-sort);
	${(props) =>
		props.$isActive &&
		`
		background-color: var(--graa-bg-lys);
		outline: 1px solid var(--nte-blaa-uu);
	`}
`;

const NoAddress = styled.div`
	padding: 8px;
`;
